import { createFeatureSelector, createSelector } from '@ngrx/store';
import {
  CourseState,
  CourseModule,
  ModuleQuiz,
  ActivityTracker,
  Certificate,
  UrlPathSegments,
  LevelInfo,
} from './models/state-models';
import { VideoConfig } from '../../shared/services/api/models/course-response';
import { TraineeAdapted } from '../../trainee/models/trainee-list.model';

const selector = <T>(mapping: (state: CourseState) => T) =>
  createSelector(selectDashboardFeature, mapping);

export const selectDashboardFeature =
  createFeatureSelector<CourseState>('dashboard');

export const getDashboardState = selector(
  (state: CourseState): CourseState => state,
);

export const getCurrentLevel = selector(
  (state: CourseState): string => state.currentModule?.level,
);

export const getCurrentLevelId = selector(
  (state: CourseState): number => state.currentModule?.levelId,
);

export const getCurrentLevelModules = selector(
  (state: CourseState): CourseModule[] =>
    state.modules.filter(
      (moduleItem) => moduleItem.level === state.currentModule?.level,
    ),
);

export const getAllModules = selector(
  (state: CourseState): CourseModule[] => state.modules,
);

export const countAllModules = selector(
  (state: CourseState): number => state.modules.length,
);

export const getModuleQuiz = selector(
  (state: CourseState): ModuleQuiz => state.currentModule.moduleQuiz,
);

export const getIsModuleQuizComplete = selector(
  (state: CourseState): boolean => state.currentModule.moduleCompleted,
);

export const getIsQuizReady = selector(
  (state: CourseState): boolean =>
    state.currentModule.videos?.every((item) => item.completed) &&
    !state.currentModule.moduleCompleted,
);

export const getActivityTracker = selector(
  (state: CourseState): ActivityTracker => state.activityTracker,
);

export const getCurrentModuleId = selector(
  (state: CourseState): number => state.currentModule.moduleId,
);

export const getCurrentModule = selector(
  (state: CourseState): CourseModule | null => state.currentModule,
);

export const getNavLevelModules = selector(
  (state: CourseState): { label: string; modules: CourseModule[] }[] => {
    const levelOne = {
      label: 'Level 1',
      modules: state.modules.filter((mod) => mod.level === 'Level 1'),
    };

    const levelTwo = {
      label: 'Level 2',
      modules: state.modules.filter((mod) => mod.level === 'Level 2'),
    };

    const levelThree = {
      label: 'Level 3',
      modules: state.modules.filter((mod) => mod.level === 'Level 3'),
    };

    return [levelOne, levelTwo, levelThree];
  },
);

export const getLevelsInfo = selector((state: CourseState): LevelInfo[] => {
  const toString = (value: string | null | undefined) => (value != null ? String(value) : '');

  const lv1Filter = state.modules.find((x) => toString(x.level).includes('1'));
  const lv2Filter = state.modules.find((x) => toString(x.level).includes('2'));
  const lv3Filter = state.modules.find((x) => toString(x.level).includes('3'));

  const lv1: LevelInfo = {
    id: lv1Filter?.levelId ?? NaN,
    title: toString(lv1Filter?.level),
  };
  const lv2: LevelInfo = {
    id: lv2Filter?.levelId ?? NaN,
    title: toString(lv2Filter?.level),
  };
  const lv3: LevelInfo = {
    id: lv3Filter?.levelId ?? NaN,
    title: toString(lv3Filter?.level),
  };

  return [lv1, lv2, lv3];
});

export const getCurrentModuleQuizThumbnail = selector(
  (state: CourseState): string => state.currentModule.takeTheQuizImage,
);

export const getCurrentModuleIndex = selector(
  (state: CourseState): number => state.currentModule.moduleIndex,
);

export const getVideos = selector(
  (state: CourseState): CourseModule['videos'] =>
    state.currentModule?.videos || [],
);

export const getCertificates = selector(
  (state: CourseState): Certificate[] => state.certificates,
);

export const getUserName = selector(
  (state: CourseState): string => state.userName,
);

export const getErrorMessage = selector(
  (state: CourseState): string => state.error,
);

export const getCulture = selector(
  (state: CourseState): string => state.culture,
);

export const getTraineeProgressId = selector(
  (state: CourseState): string => state.traineeProgressId,
);

export const getCookiePreferences = selector(
  (state: CourseState): boolean => state.cookiePreferencesAccepted,
);

export const getCurrentPath = selector(
  (state: CourseState): UrlPathSegments => state.urlPath,
);

export const getCurrentVideo = selector(
  (state: CourseState): VideoConfig => state.currentVideo,
);

export const getAvailableCertificate = selector(
  (state: CourseState): Certificate | undefined => {
    return state.certificates.find(
      (cert) =>
        cert.isCertificateAvailable && cert.level === state.currentModule.level,
    );
  },
);

export const getTraineeList = selector(
  (state: CourseState): TraineeAdapted[] => {
    return state.traineeManagement.traineeList;
  },
);

export const getCurrentTrainee = selector(
  (state: CourseState): TraineeAdapted | null => {
    return state.traineeManagement.selectedTraineeDetails ?? null;
  },
);

export const getRemainingLicenses = selector((state: CourseState): number => {
  return state.traineeManagement.licensesRemaining;
});

export const getTraineeLoaded = selector(
  (state: CourseState): boolean => {
    return state.traineeManagement.traineeManagementInfoLoaded;
  }
);

export const getTraineeManagementAccess = selector(
  (state: CourseState): boolean => {
    return state.traineeManagement.canAccessTraineeManagement;
  },
);

export const getOtherValidEmails = selector((state: CourseState): string[] => {
  return state.traineeManagement.otherEmails;
});

export const getLevelIDs = selector(
  (state: CourseState): { level1: number; level2: number; level3: number } => {
    const traineeManagement = state.traineeManagement;
    return {
      level1: traineeManagement.levelOneId,
      level2: traineeManagement.levelTwoId,
      level3: traineeManagement.levelThreeId,
    };
  },
);
